import { Container, Graphics, Rectangle, RenderTexture, Sprite, Text, Texture } from "pixi.js"
import { EmberRosterEntry, EmberRosterRole, Entity, EntityId } from "game-common/models"
import { locateInventoryActiveItem } from "game-common/item/item"
import { GuiManager } from "../gui_manager"
import { LpcLayered } from "../sprites/lpc_layered"
import { FlameBackdrop } from "./flame_backdrop"
import { TextRenderer } from "../text/text_renderer"
// Reached for only to photograph a dying card (see captureCard); imported the same way
// effects/sprite_shatter.ts reaches for it, and read lazily so the cycle never bites at load.
import { PixiJsClientRenderer } from "../pixijs_client_renderer"
import { disposeTextTextures } from "../text/dispose_text"
import { BubbleTarget, MAX_BUBBLE_H, RosterSpeechBubble } from "./roster_speech"
import { RosterSpeechLane } from "./roster_speech_lane"
// The card's hand-placed geometry and colour ramps (pure, and tested on their own).
import {
    BADGE_SIZE,
    BADGE_X,
    BADGE_Y,
    DAMAGE_GHOST,
    DAMAGE_NUMBER_COLOR,
    DAMAGE_NUMBER_SIZE,
    DAMAGE_NUMBER_Y,
    DAMAGE_RING,
    DAMAGE_WASH,
    HP_H,
    HP_MARGIN,
    HP_Y,
    HUNGER_H,
    HUNGER_PULSE_MS,
    HUNGER_URGENT,
    HUNGER_Y,
    hpColor,
    hungerColor,
    INNER_MARGIN,
    INNER_RADIUS,
    NAME_MAX_CHARS,
    NAME_Y,
    PANEL_PAD,
    PORTRAIT_SCALE,
    PORTRAIT_X,
    PORTRAIT_Y,
    ROLE_EMOJI,
    SCRIM_H,
    SLOT_H,
    SLOT_W,
} from "./ember_roster_card"
import { DamageFrame, RosterDamageFx } from "./roster_damage"
import {
    DEATH_MS,
    FragmentSeed,
    GREY_MS,
    fragmentAt,
    greyAt,
    isFinished,
    isShattering,
    seedFragments,
} from "./roster_death"

const ROLE_COLORS: Record<EmberRosterRole, number> = {
    lord: 0xffd23f,
    defender: 0xff8a5c,
    forager: 0x7be08a,
}

const ROLE_ORDER: Record<EmberRosterRole, number> = { lord: 0, defender: 1, forager: 2 }

// Uniform scale for the whole widget (portraits, text, emoji, spacing) — cheaper and more reliable
// than re-tuning every geometry constant below.
const WIDGET_SCALE = 0.8

// Child z-order within a card (container.sortableChildren) so the fire backdrop sits behind the
// portrait but above the frame, regardless of add/rebuild order.
const Z_CHROME = 0
const Z_FLAME = 1
const Z_PORTRAIT = 2
const Z_SCRIM = 3
// The damage wash goes over the portrait but UNDER the name and stat bars, so a card can shout that
// it was hit without hiding the numbers the player is being told to look at.
const Z_WASH = 4
const Z_BADGE = 5
const Z_NAME = 6
const Z_HP = 7
const Z_HUNGER = 8
const Z_SELECTION = 9
const Z_SPEAKING = 10
const Z_DAMAGE_RING = 11
const Z_DAMAGE_NUMBER = 12
// The grey a dead member's card goes under sits over everything else on it.
const Z_DEATH_PALL = 13

// Ash: mid-grey, and just short of opaque (see drawPall).
const PALL_COLOR = 0x5a5f5a
const PALL_MAX_ALPHA = 0.88

// Offscreen speech lane: a camp member speaking while OUT of the player's view gets their line
// shown over their roster card. The rules (one bubble at a time, dwell, queue cap, card flash) are
// in roster_speech_lane.ts; this file only draws what that decides. The lane's height comes from
// the bubble itself so the two can't drift apart — it is reserved whether or not anyone is talking,
// so the follow-cam PiP sitting above the strip never jumps.
const SPEECH_LANE_H = MAX_BUBBLE_H

interface RosterSlot {
    container: Container
    portrait: LpcLayered
    mask: Graphics
    badge: Text
    nameText: Text
    hpBar: Graphics
    // Recruits only: how full they are (1 = just ate, 0 = about to walk out). Hidden on the lord's
    // card, which has no hunger arc.
    hungerBar: Graphics
    hunger?: number
    selection: Graphics
    appearanceKey: string
    role: EmberRosterRole
    name?: string
    hp?: number
    hpMax?: number
    // Where the card sits at rest, so the damage shake can move it off that and back without the
    // next roster tick fighting the animation for `container.x`.
    baseX: number
    // Which column of the strip the card occupies. A card whose member has died goes on holding its
    // column while it breaks up, and the survivors lay out around the gap (see `layout`).
    column: number
    // Lord only: animated campfire-strength particle backdrop + its clip mask, and the current
    // strength (0..1) driving it.
    flame?: FlameBackdrop
    flameMask?: Graphics
    fireStrength: number
    // Role-coloured ring that flashes when this member speaks offscreen.
    speaking: Graphics
    speakingRole?: EmberRosterRole
    // Damage effect: the red wash over the portrait, the ring around the frame, and the floating
    // "-N" (built on the first hit the card takes, then reused). What they do is decided by
    // RosterDamageFx; the slot only holds the objects and what was last drawn.
    wash: Graphics
    damageRing: Graphics
    damageNumber?: Text
    damageSeq: number
    // Last ghost position drawn into the HP bar, so a card only repaints its bar when the pale
    // segment has actually moved (it spends a fifth of the effect held still).
    ghostRatio?: number
}

// A card on its way out: its member has died, so the card greys over, breaks into shards and takes
// its column with it. The slot is kept whole until the very end (rather than torn down at the start)
// because the grey phase is the card ITSELF greying -- portrait, name, bars and all.
interface DyingCard {
    slot: RosterSlot
    column: number
    elapsedMs: number
    pall: Graphics
    // The ash the card greys under. An OVERLAY rather than a ColorMatrixFilter: a filter on the card
    // container renders it as nothing here (pixi has to derive a filter area from the card's bounds,
    // and the paperdoll portrait inside does not give it a usable one), and a filtered card also
    // costs a render target per frame on hardware this has to run on.
    // Built at the moment the card breaks: a snapshot of the grey card, sliced into shards.
    shards?: Container
    pieces?: Sprite[]
    seeds?: FragmentSeed[]
    texture?: RenderTexture
    shattered: boolean
}

// Clickable HUD panel of the ember camp's notable NPCs (lord / defenders / foragers). Fed by the
// per-room roster broadcast (Protocol.type_updateEmberRoster). Each card is a framed, zoomed-in
// portrait (the LpcLayered paperdoll from the inventory view) filling the card like a background,
// with an emoji role badge, a short name and an HP bar overlaid. Clicking opens the follow-cam PiP.
export class EmberRosterWidget extends Graphics {
    guiManager: GuiManager
    private slots: Map<EntityId, RosterSlot> = new Map()
    private selectedEntityId?: EntityId

    // Offscreen speech: the rules live in the lane, the bubble draws what it decides.
    private speechBubble: RosterSpeechBubble
    private speechLane = new RosterSpeechLane()
    // Hits taken by camp members, read off the health each roster tick reports. The curves and
    // timings live in roster_damage.ts; this widget only draws the frame it is handed.
    private damageFx = new RosterDamageFx()
    // Cards whose member has died: greying, then breaking apart, and holding their column in the
    // strip until the last shard has gone (see roster_death.ts). Keyed by the entity that left, so a
    // member who somehow comes back gets a fresh card rather than the one that is falling apart.
    private dying: Map<EntityId, DyingCard> = new Map()
    // The living cards in display order, remembered so the strip can be laid out again the moment a
    // death finishes rather than waiting for the next roster tick to close the gap.
    private order: EntityId[] = []
    // Free-running clock for the starving-member pulse, shared by every card so they breathe together
    // rather than each on its own phase.
    private pulseMs = 0

    constructor(guiManager: GuiManager) {
        super()
        this.guiManager = guiManager
        this.scale.set(WIDGET_SCALE)
        this.speechBubble = new RosterSpeechBubble()
        this.addChild(this.speechBubble)
        this.exposeDevTriggers()
    }

    // Browser-console triggers, in the same shape as window.playerRing / window.spriteShadow:
    //
    //     rosterHit()        // every card takes a middling hit
    //     rosterHit(0.4)     // ...takes 40% of its health off
    //     rosterKill()       // the last card on the strip dies and breaks apart
    //     rosterWidget       // the strip itself: cardCount(), isDying(id), deathPhase(id)
    //
    // Neither reaction can be looked at on demand -- one needs something off-screen to land a blow,
    // the other needs somebody to die -- so there would otherwise be no way to tune the shake, the
    // colours or the shatter. The health rosterHit pretends was lost is corrected by the next roster
    // tick half a second later; the card rosterKill takes is rebuilt by it.
    private exposeDevTriggers = () => {
        if (typeof window === "undefined") {
            return
        }
        ;(window as any).rosterWidget = this
        ;(window as any).rosterKill = (name?: string): string => {
            const entry = Array.from(this.slots.entries())
                .reverse()
                .find(([, slot]) => !name || (slot.name || "").toLowerCase().includes(name.toLowerCase()))
            if (!entry) {
                return `[rosterKill] no card matching ${name ?? "(any)"}`
            }
            this.beginDeath(entry[0], entry[1])
            this.layout()
            this.guiManager.positionEmberRoster()
            return entry[0]
        }
        ;(window as any).rosterHit = (fraction: number = 0.18): string => {
            this.slots.forEach((slot, entityId) => {
                const hpMax = slot.hpMax ?? 20
                const hp = slot.hp ?? hpMax
                const hit = Math.max(0, hp - hpMax * fraction)
                this.damageFx.hit(entityId, hp, hit, hpMax)
                slot.hp = hit
                this.drawHpBar(slot.hpBar, hit, hpMax, slot.ghostRatio)
            })
            return `[rosterHit] ${this.slots.size} card(s)`
        }
    }

    private appearanceKeyFor = (entry: EmberRosterEntry): string =>
        entry.appearance ? JSON.stringify(entry.appearance) : ""

    private truncate = (name?: string): string => {
        if (!name) {
            return ""
        }
        return name.length > NAME_MAX_CHARS ? name.slice(0, NAME_MAX_CHARS - 1) + "…" : name
    }

    // Build the zoomed front-facing portrait from a synthetic entity, mirroring the inventory-view
    // portrait (dynamic_dialog.ts / EntityDisplayer). Clipped to the card via `mask` by the caller.
    private buildPortrait = (entry: EmberRosterEntry): LpcLayered => {
        const entity = new Entity()
        entity.weapon = { weaponType: locateInventoryActiveItem(entry.inventory, "weapon")?.type as any }
        entity.appearance = entry.appearance
        entity.inventory = entry.inventory

        const portrait = new LpcLayered(entry.entityId)
        try {
            portrait.updatePaperdoll(entity)
        } catch (e) {
            // Malformed appearance data must not take down the whole roster — leave it blank.
            console.warn(`[EmberRoster] portrait build failed for ${entry.entityId}`, e)
        }
        portrait.scale.set(PORTRAIT_SCALE)
        portrait.x = PORTRAIT_X
        portrait.y = PORTRAIT_Y
        return portrait
    }

    private makeMask = (): Graphics => {
        const g = new Graphics()
        g.beginFill(0xffffff)
        g.drawRoundedRect(
            INNER_MARGIN,
            INNER_MARGIN,
            SLOT_W - INNER_MARGIN * 2,
            SLOT_H - INNER_MARGIN * 2,
            INNER_RADIUS,
        )
        g.endFill()
        return g
    }

    private makeBadge = (role: EmberRosterRole): Text => {
        // No tint: keep the emoji's own colors.
        const t = new TextRenderer().fontSize(BADGE_SIZE).render(ROLE_EMOJI[role]) as Text
        t.x = BADGE_X
        t.y = BADGE_Y
        return t
    }

    private makeNameText = (name?: string): Text => {
        const t = new TextRenderer().size("tiny").render(this.truncate(name)) as Text
        t.tint = 0xffffff
        t.x = Math.round(SLOT_W / 2 - t.width / 2)
        t.y = NAME_Y
        return t
    }

    // `ghostRatio` is the fighting-game "health you just lost": a pale segment left standing where
    // the bar was before the hit, drawn UNDER the live fill so only the difference shows, and drained
    // away by the damage effect a beat later. Undefined (the resting case) draws the bar as before.
    private drawHpBar = (bar: Graphics, hp?: number, hpMax?: number, ghostRatio?: number) => {
        bar.clear()
        const w = SLOT_W - HP_MARGIN * 2
        bar.beginFill(0x000000, 0.6)
        bar.drawRoundedRect(0, 0, w, HP_H, 2)
        bar.endFill()
        if (ghostRatio !== undefined && ghostRatio > 0) {
            bar.beginFill(DAMAGE_GHOST, 0.9)
            bar.drawRoundedRect(0, 0, Math.max(1, w * Math.min(1, ghostRatio)), HP_H, 2)
            bar.endFill()
        }
        if (hp !== undefined && hpMax && hpMax > 0) {
            const ratio = Math.max(0, Math.min(1, hp / hpMax))
            const color = hpColor(ratio)
            if (ratio > 0) {
                bar.beginFill(color, 0.95)
                bar.drawRoundedRect(0, 0, Math.max(1, w * ratio), HP_H, 2)
                bar.endFill()
            }
        }
        bar.x = HP_MARGIN
        bar.y = HP_Y
    }

    // The hunger bar: the recruit's whole food arc as one falling bar, drawn a shade thinner than the
    // HP bar above it and in warm food colours (amber -> orange -> ember red) so the two rows can't be
    // read as the same stat. The server sends no hunger for the lord, whose row stays empty.
    private drawHungerBar = (bar: Graphics, hunger?: number) => {
        bar.clear()
        bar.visible = hunger !== undefined
        if (hunger === undefined) {
            return
        }
        const w = SLOT_W - HP_MARGIN * 2
        bar.beginFill(0x000000, 0.6)
        bar.drawRoundedRect(0, 0, w, HUNGER_H, 2)
        bar.endFill()
        const ratio = Math.max(0, Math.min(1, hunger))
        const color = hungerColor(ratio)
        if (ratio > 0) {
            bar.beginFill(color, 0.95)
            bar.drawRoundedRect(0, 0, Math.max(1, w * ratio), HUNGER_H, 2)
            bar.endFill()
        }
        bar.x = HP_MARGIN
        bar.y = HUNGER_Y
    }

    // The card frame (dark, so the inset portrait reads as a bordered background) plus a bottom
    // scrim and a small chip behind the badge, all in one Graphics behind the portrait/overlays.
    private drawChrome = (chrome: Graphics) => {
        chrome.clear()
        // Frame / margin.
        chrome.beginFill(0x0a0d08, 0.9)
        chrome.lineStyle(1, 0xffffff, 0.1)
        chrome.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
        chrome.endFill()
    }

    private drawScrim = (scrim: Graphics) => {
        scrim.clear()
        scrim.beginFill(0x000000, 0.5)
        scrim.drawRoundedRect(
            INNER_MARGIN,
            SLOT_H - INNER_MARGIN - SCRIM_H,
            SLOT_W - INNER_MARGIN * 2,
            SCRIM_H,
            INNER_RADIUS,
        )
        scrim.endFill()
        // Chip behind the emoji badge (top-left) for contrast over any portrait.
        scrim.beginFill(0x000000, 0.45)
        scrim.drawRoundedRect(BADGE_X - 2, BADGE_Y - 1, BADGE_SIZE + 5, BADGE_SIZE + 3, 3)
        scrim.endFill()
    }

    // The flash a card gives when its member speaks from offscreen — the immediate acknowledgement
    // that lets extra speakers wait in the queue without their line feeling dropped.
    private drawSpeakingRing = (ring: Graphics, role: EmberRosterRole) => {
        ring.clear()
        ring.lineStyle(2, ROLE_COLORS[role], 1)
        ring.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
    }

    // The two halves of a hit's flash, drawn once per card and then only faded: a wash filling the
    // portrait area and a ring around the frame (which outlives the wash, so the card stays marked
    // as the one that was hit).
    private drawWash = (wash: Graphics) => {
        wash.clear()
        wash.beginFill(DAMAGE_WASH, 1)
        wash.drawRoundedRect(
            INNER_MARGIN,
            INNER_MARGIN,
            SLOT_W - INNER_MARGIN * 2,
            SLOT_H - INNER_MARGIN * 2,
            INNER_RADIUS,
        )
        wash.endFill()
    }

    private drawDamageRing = (ring: Graphics) => {
        ring.clear()
        ring.lineStyle(2, DAMAGE_RING, 1)
        ring.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
    }

    // The grey a dead member's card goes under. Left just short of opaque, so the shape of the card
    // -- the portrait, the name -- is still faintly there under the ash rather than replaced by a
    // plain grey oblong.
    private drawPall = (pall: Graphics) => {
        pall.clear()
        pall.beginFill(PALL_COLOR, 1)
        pall.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
        pall.endFill()
    }

    private drawSelection = (selection: Graphics, role: EmberRosterRole) => {
        selection.clear()
        selection.lineStyle(2, ROLE_COLORS[role], 0.95)
        selection.drawRoundedRect(1, 1, SLOT_W - 2, SLOT_H - 2, INNER_RADIUS + 1)
    }

    // Add (lord) or remove (non-lord) the animated fire-particle backdrop for a slot, matching its
    // role. The flame sits behind the portrait, clipped to the same inset rounded rect.
    private ensureFlame = (slot: RosterSlot) => {
        const isLord = slot.role === "lord"
        if (isLord && !slot.flame) {
            const mask = this.makeMask()
            const flame = new FlameBackdrop(SLOT_W, SLOT_H)
            flame.mask = mask
            flame.zIndex = Z_FLAME
            slot.container.addChild(mask, flame)
            slot.flame = flame
            slot.flameMask = mask
        } else if (!isLord && slot.flame) {
            slot.flame.mask = null
            slot.container.removeChild(slot.flame, slot.flameMask!)
            slot.flame.destroy()
            slot.flameMask?.destroy()
            slot.flame = undefined
            slot.flameMask = undefined
        }
    }

    // Advance the fire-particle backdrops, the starving pulse, the damage effects and the offscreen
    // speech lane (called each frame by GuiManager).
    animate = (dtMs: number) => {
        this.pulseMs = (this.pulseMs + dtMs) % HUNGER_PULSE_MS
        // 1 -> 0.55 -> 1 over the period. Done with alpha rather than a redraw so a pulsing card
        // costs nothing beyond the value itself.
        const pulse = 0.55 + 0.45 * (0.5 + 0.5 * Math.cos((this.pulseMs / HUNGER_PULSE_MS) * Math.PI * 2))
        this.damageFx.update(dtMs)
        this.slots.forEach((slot, entityId) => {
            slot.flame?.update(dtMs, slot.fireStrength)
            slot.hungerBar.alpha = slot.hunger !== undefined && slot.hunger <= HUNGER_URGENT ? pulse : 1
            this.applyDamage(slot, this.damageFx.frame(entityId))
        })
        this.updateSpeech(dtMs)
        this.updateDeaths(dtMs)
    }

    // Put one card into the state a hit's frame asks for -- or back to rest when there is no hit.
    // The resting path touches nothing it doesn't have to, so an undamaged strip (the normal case)
    // costs a handful of assignments per card per frame and no redraw at all.
    private applyDamage = (slot: RosterSlot, frame: DamageFrame | null) => {
        if (!frame) {
            if (slot.damageSeq === 0) {
                return
            }
            slot.damageSeq = 0
            slot.container.x = slot.baseX
            slot.container.y = 0
            slot.container.scale.set(1)
            slot.wash.visible = false
            slot.damageRing.visible = false
            if (slot.damageNumber) {
                slot.damageNumber.visible = false
            }
            if (slot.ghostRatio !== undefined) {
                slot.ghostRatio = undefined
                this.drawHpBar(slot.hpBar, slot.hp, slot.hpMax)
            }
            return
        }

        // Scale about the card's centre without a pivot (which `selectedCardAnchor` and the layout
        // below both read `container.x` raw for), by taking back half of what the growth adds.
        const grow = frame.scale - 1
        slot.container.scale.set(frame.scale)
        slot.container.x = slot.baseX + frame.shakeX - (grow * SLOT_W) / 2
        slot.container.y = frame.shakeY - (grow * SLOT_H) / 2

        slot.wash.alpha = frame.flashAlpha
        slot.wash.visible = frame.flashAlpha > 0.01
        slot.damageRing.alpha = frame.ringAlpha
        slot.damageRing.visible = frame.ringAlpha > 0.01

        // The ghost holds still for the first fifth of the effect; only repaint the bar once it has
        // actually moved.
        if (slot.ghostRatio === undefined || Math.abs(slot.ghostRatio - frame.ghostRatio) > 0.004) {
            slot.ghostRatio = frame.ghostRatio
            this.drawHpBar(slot.hpBar, slot.hp, slot.hpMax, frame.ghostRatio)
        }

        const damageNumber = this.ensureDamageNumber(slot)
        if (slot.damageSeq !== frame.seq) {
            slot.damageSeq = frame.seq
            damageNumber.text = `-${frame.amount}`
            damageNumber.x = Math.round(SLOT_W / 2 - damageNumber.width / 2)
        }
        damageNumber.y = DAMAGE_NUMBER_Y - frame.numberRise
        damageNumber.alpha = frame.numberAlpha
        damageNumber.visible = frame.numberAlpha > 0.01
    }

    // The floating "-N" is built on the card's FIRST hit and then reused (its text is re-rasterized
    // per hit, which is once every 500ms at worst) -- a fresh Text per hit would churn a canvas-backed
    // texture through the GPU for every blow landed in the camp.
    private ensureDamageNumber = (slot: RosterSlot): Text => {
        if (!slot.damageNumber) {
            const text = new TextRenderer()
                .fontSize(DAMAGE_NUMBER_SIZE)
                .bold()
                .color(DAMAGE_NUMBER_COLOR)
                .render("") as Text
            text.zIndex = Z_DAMAGE_NUMBER
            slot.container.addChild(text)
            slot.damageNumber = text
        }
        return slot.damageNumber
    }

    // Hand the lane this frame's members and whatever they've said out of the player's sight, then
    // draw whichever single line it picked, plus the card flashes it wants.
    private updateSpeech = (dtMs: number) => {
        const members = Array.from(this.slots.entries()).map(([entityId, slot]) => ({
            entityId,
            name: slot.name || "",
        }))
        this.speechLane.update(dtMs, members, this.guiManager.getOffscreenSpeech)

        // Hand the bubble this frame's target; it owns the fade and the swap between speakers.
        const line = this.speechLane.currentLine()
        const slot = line ? this.slots.get(line.entityId) : undefined
        const target: BubbleTarget | null =
            line && slot
                ? {
                      key: `${line.entityId}:${line.text}`,
                      name: line.name,
                      text: line.text,
                      tailX: slot.baseX + SLOT_W / 2,
                      tailY: -PANEL_PAD,
                      clampRight: this.slots.size * SLOT_W + PANEL_PAD,
                      color: ROLE_COLORS[slot.role],
                  }
                : null
        this.speechBubble.update(dtMs, target)

        this.slots.forEach((s, entityId) => {
            const alpha = this.speechLane.pulseAlpha(entityId)
            if (alpha > 0 && s.speakingRole !== s.role) {
                this.drawSpeakingRing(s.speaking, s.role)
                s.speakingRole = s.role
            }
            s.speaking.alpha = alpha
            s.speaking.visible = alpha > 0
        })
    }

    // A member has left the roster, which in an ember camp means they died. Hand their card over to
    // the death sequence instead of tearing it down: it holds its column, greys over, and breaks
    // apart (roster_death.ts owns the timings).
    private beginDeath = (entityId: EntityId, slot: RosterSlot) => {
        this.slots.delete(entityId)
        this.speechLane.forget(entityId)
        this.damageFx.forget(entityId)
        this.order = this.order.filter(id => id !== entityId)

        // A dead member is not somebody you can still click through to, and the card's live
        // decoration (the selection ring, a speech flash, a half-finished damage flash) has no
        // business outliving them.
        slot.container.interactive = false
        slot.container.cursor = "default"
        slot.selection.visible = false
        slot.speaking.visible = false
        slot.wash.visible = false
        slot.damageRing.visible = false
        if (slot.damageNumber) {
            slot.damageNumber.visible = false
        }
        slot.container.scale.set(1)
        if (this.selectedEntityId === entityId) {
            this.selectedEntityId = undefined
        }

        const pall = new Graphics()
        this.drawPall(pall)
        pall.alpha = 0
        pall.zIndex = Z_DEATH_PALL
        slot.container.addChild(pall)

        this.dying.set(entityId, {
            slot,
            column: slot.column,
            elapsedMs: 0,
            pall,
            shattered: false,
        })
    }

    // Advance every dying card: drain its colour, break it apart at the halfway mark, fly the shards,
    // and take the whole thing off the strip when it is done.
    private updateDeaths = (dtMs: number) => {
        let finished = false
        this.dying.forEach((card, entityId) => {
            card.elapsedMs += dtMs

            if (!card.shattered) {
                // Over the WHOLE card, bars and name included -- the member is gone, so nothing on
                // the card is still telling the player anything.
                card.pall.alpha = PALL_MAX_ALPHA * greyAt(card.elapsedMs)
            }

            if (!card.shattered && isShattering(card.elapsedMs)) {
                this.shatterCard(card)
            }

            if (card.shattered && card.seeds && card.pieces) {
                const shatterMs = card.elapsedMs - GREY_MS
                card.seeds.forEach((seed, i) => {
                    const piece = card.pieces![i]
                    const motion = fragmentAt(shatterMs, seed)
                    if (!motion) {
                        return
                    }
                    piece.x = seed.x + seed.w / 2 + motion.dx
                    piece.y = seed.y + seed.h / 2 + motion.dy
                    piece.angle = motion.angle
                    piece.alpha = motion.alpha
                })
            }

            if (isFinished(card.elapsedMs)) {
                this.endDeath(entityId, card)
                finished = true
            }
        })
        if (finished) {
            // The gap closes the moment the last shard has gone, rather than on whichever roster tick
            // happens to come next.
            this.layout()
            this.guiManager.positionEmberRoster()
        }
    }

    // Break the (by now grey) card into shards: snapshot it to a texture, slice that into a grid of
    // little sprites, and hide the card itself. Without a renderer -- a test, or a context that is
    // gone -- there is nothing to photograph and nothing being watched either, so the card just goes
    // and the death runs out its clock with the column still held.
    private shatterCard = (card: DyingCard) => {
        card.shattered = true
        const texture = this.captureCard(card.slot)
        if (!texture) {
            card.slot.container.visible = false
            return
        }

        const seeds = seedFragments(SLOT_W, SLOT_H)
        const shards = new Container()
        shards.position.set(card.column * SLOT_W, 0)
        const pieces = seeds.map(seed => {
            const piece = new Sprite(new Texture(texture.baseTexture, new Rectangle(seed.x, seed.y, seed.w, seed.h)))
            // Spun about their own middles, so a shard tumbles rather than swinging around a corner.
            piece.anchor.set(0.5)
            piece.x = seed.x + seed.w / 2
            piece.y = seed.y + seed.h / 2
            shards.addChild(piece)
            return piece
        })
        this.addChild(shards)

        card.texture = texture
        card.shards = shards
        card.pieces = pieces
        card.seeds = seeds
        card.slot.container.visible = false
    }

    // Snapshot one card into a texture of its own. The card is drawn at its own origin (its position
    // in the strip is put back straight afterwards) so the shards can be placed by the grid alone.
    private captureCard = (slot: RosterSlot): RenderTexture | null => {
        const renderer = (PixiJsClientRenderer as any)._app?.renderer
        if (!renderer) {
            return null
        }
        try {
            const resolution = renderer.resolution || 1
            const texture = RenderTexture.create({ width: SLOT_W, height: SLOT_H, resolution })
            const savedX = slot.container.x
            const savedY = slot.container.y
            slot.container.position.set(0, 0)
            renderer.render(slot.container, { renderTexture: texture, clear: true })
            slot.container.position.set(savedX, savedY)
            return texture
        } catch (e) {
            // A card that can't be photographed must not take the roster down with it.
            console.warn("[EmberRoster] card snapshot failed", e)
            return null
        }
    }

    private endDeath = (entityId: EntityId, card: DyingCard) => {
        this.dying.delete(entityId)
        card.pall.destroy()
        if (card.shards) {
            this.removeChild(card.shards)
            card.shards.destroy({ children: true })
        }
        // The shards' sub-textures share this base texture, so it goes last, once they are gone.
        card.texture?.destroy(true)
        this.disposeSlot(card.slot)
    }

    private disposeSlot = (slot: RosterSlot) => {
        slot.speaking.destroy()
        slot.wash.destroy()
        slot.damageRing.destroy()
        disposeTextTextures(slot.container)
        slot.portrait.mask = null
        slot.portrait.remove()
        if (slot.flame) {
            slot.flame.mask = null
            slot.flame.destroy()
            slot.flameMask?.destroy()
        }
        this.removeChild(slot.container)
    }

    private drawPanel = (count: number) => {
        this.clear()
        if (count < 1) {
            return
        }
        const w = count * SLOT_W + PANEL_PAD * 2
        const h = SLOT_H + PANEL_PAD * 2
        this.beginFill(0x10140f, 0.72)
        this.lineStyle(1, 0xffffff, 0.12)
        this.drawRoundedRect(-PANEL_PAD, -PANEL_PAD, w, h, 8)
        this.endFill()
    }

    update = (roster: EmberRosterEntry[]) => {
        // At most ONE hit sound per tick, however many members were hurt in it. The roster speaks
        // twice a second, so a wave mauling the whole camp would otherwise stack three or four
        // sounds into the same frame and arrive as a blur rather than as blows.
        let heardAHit = false

        // Stable order (lord, defenders, foragers) so cards don't shuffle between ticks.
        const ordered = [...roster].sort((a, b) => {
            const o = ROLE_ORDER[a.role] - ROLE_ORDER[b.role]
            return o !== 0 ? o : a.entityId.localeCompare(b.entityId)
        })

        const incoming = new Set(ordered.map(e => e.entityId))
        for (const [entityId, slot] of Array.from(this.slots.entries())) {
            if (!incoming.has(entityId)) {
                // Off the roster means dead (or gone): the card doesn't just disappear, it dies.
                this.beginDeath(entityId, slot)
            }
        }

        ordered.forEach(entry => {
            const appearanceKey = this.appearanceKeyFor(entry)
            let slot = this.slots.get(entry.entityId)

            if (!slot) {
                const container = new Container()
                container.interactive = true
                container.cursor = "pointer"
                container.hitArea = new Rectangle(0, 0, SLOT_W, SLOT_H)
                container.sortableChildren = true

                const chrome = new Graphics()
                this.drawChrome(chrome)
                chrome.zIndex = Z_CHROME
                const mask = this.makeMask()
                const portrait = this.buildPortrait(entry)
                portrait.mask = mask
                portrait.zIndex = Z_PORTRAIT
                const scrim = new Graphics()
                this.drawScrim(scrim)
                scrim.zIndex = Z_SCRIM
                const badge = this.makeBadge(entry.role)
                badge.zIndex = Z_BADGE
                const nameText = this.makeNameText(entry.name)
                nameText.zIndex = Z_NAME
                const hpBar = new Graphics()
                this.drawHpBar(hpBar, entry.hp, entry.hpMax)
                hpBar.zIndex = Z_HP
                const hungerBar = new Graphics()
                this.drawHungerBar(hungerBar, entry.hunger)
                hungerBar.zIndex = Z_HUNGER
                const selection = new Graphics()
                selection.visible = false
                selection.zIndex = Z_SELECTION
                const speaking = new Graphics()
                speaking.visible = false
                speaking.zIndex = Z_SPEAKING
                const wash = new Graphics()
                this.drawWash(wash)
                wash.visible = false
                wash.zIndex = Z_WASH
                const damageRing = new Graphics()
                this.drawDamageRing(damageRing)
                damageRing.visible = false
                damageRing.zIndex = Z_DAMAGE_RING

                container.addChild(
                    chrome,
                    portrait,
                    mask,
                    scrim,
                    badge,
                    nameText,
                    hpBar,
                    hungerBar,
                    selection,
                    speaking,
                    wash,
                    damageRing,
                )
                container.on("pointerdown", () => this.onSlotClick(entry.entityId))

                slot = {
                    container,
                    portrait,
                    mask,
                    badge,
                    nameText,
                    hpBar,
                    hungerBar,
                    hunger: entry.hunger,
                    selection,
                    appearanceKey,
                    role: entry.role,
                    name: entry.name,
                    hp: entry.hp,
                    hpMax: entry.hpMax,
                    baseX: 0,
                    column: 0,
                    fireStrength: entry.fireStrength ?? 0,
                    speaking,
                    wash,
                    damageRing,
                    damageSeq: 0,
                }
                this.ensureFlame(slot)
                this.slots.set(entry.entityId, slot)
                this.addChild(container)
            } else {
                // Rebuild only what actually changed (portrait rebuild + Text textures are costly).
                if (slot.appearanceKey !== appearanceKey) {
                    slot.portrait.mask = null
                    slot.portrait.remove()
                    const portrait = this.buildPortrait(entry)
                    portrait.mask = slot.mask
                    portrait.zIndex = Z_PORTRAIT
                    slot.container.addChild(portrait)
                    slot.portrait = portrait
                    slot.appearanceKey = appearanceKey
                }
                if (slot.role !== entry.role) {
                    disposeTextTextures(slot.badge)
                    slot.container.removeChild(slot.badge)
                    slot.badge = this.makeBadge(entry.role)
                    slot.badge.zIndex = Z_BADGE
                    slot.container.addChild(slot.badge)
                    slot.role = entry.role
                }
                if (slot.name !== entry.name) {
                    disposeTextTextures(slot.nameText)
                    slot.container.removeChild(slot.nameText)
                    slot.nameText = this.makeNameText(entry.name)
                    slot.nameText.zIndex = Z_NAME
                    slot.container.addChild(slot.nameText)
                    slot.name = entry.name
                }
                if (slot.hp !== entry.hp || slot.hpMax !== entry.hpMax) {
                    // A member whose health went DOWN since the last tick was hurt -- the roster
                    // carries no hit event of its own, and this is the one place both numbers are
                    // known. Healing and a first sighting fall through to a plain redraw.
                    if (slot.hp !== undefined && entry.hp !== undefined && entry.hpMax) {
                        this.damageFx.hit(entry.entityId, slot.hp, entry.hp, entry.hpMax)
                        if (entry.hp < slot.hp && !heardAHit) {
                            // Somebody in the camp is being hurt out of sight. The card says so; the
                            // sound is what gets the player to look at it. GuiManager keeps quiet for
                            // a member close enough that the world already played their hit.
                            this.guiManager.playOffscreenHit(entry.entityId)
                            heardAHit = true
                        }
                    }
                    slot.hp = entry.hp
                    slot.hpMax = entry.hpMax
                    this.drawHpBar(slot.hpBar, entry.hp, entry.hpMax, slot.ghostRatio)
                }
                if (slot.hunger !== entry.hunger) {
                    this.drawHungerBar(slot.hungerBar, entry.hunger)
                    slot.hunger = entry.hunger
                }
                slot.fireStrength = entry.fireStrength ?? 0
                this.ensureFlame(slot)
            }

        })

        this.order = ordered.map(entry => entry.entityId)
        this.layout()
        this.applySelection()
    }

    // Place every card in the strip and redraw the panel behind them. Cards whose member has died
    // keep the column they had, and the living lay out around those gaps -- so a card breaking apart
    // is never slid under by a survivor, and the strip only closes up once it has gone.
    //
    // Called on each roster tick AND the moment a death finishes, because the gap has to close then
    // rather than whenever the next tick happens to arrive.
    private layout = () => {
        const total = this.order.length + this.dying.size

        // Dying cards claim their column first, lowest first. An index that no longer exists (the
        // last card of the strip died) falls to the nearest one that does.
        const taken = new Set<number>()
        Array.from(this.dying.values())
            .sort((a, b) => a.column - b.column)
            .forEach(card => {
                let column = Math.min(Math.max(0, card.column), Math.max(0, total - 1))
                while (taken.has(column)) {
                    column = column + 1 >= total ? 0 : column + 1
                }
                taken.add(column)
                card.column = column
                card.slot.column = column
                this.place(card.slot, column)
                card.shards?.position.set(column * SLOT_W, 0)
            })

        let column = 0
        this.order.forEach(entityId => {
            const slot = this.slots.get(entityId)
            if (!slot) {
                return
            }
            while (taken.has(column)) {
                column++
            }
            slot.column = column
            this.place(slot, column)
            this.applyDamage(slot, this.damageFx.frame(entityId))
            column++
        })

        this.drawPanel(total)
    }

    // The card's resting place. Its ACTUAL position is set by applyDamage, which is what holds the
    // shake -- a roster tick landing mid-jolt would otherwise snap the card back.
    private place = (slot: RosterSlot, column: number) => {
        slot.baseX = column * SLOT_W
        slot.container.x = slot.baseX
        slot.container.y = 0
    }

    // Take the whole strip down at once, with no death animation: this is a ROOM CHANGE, not a camp
    // losing its people, and a card cannot break apart over a room the player has already left.
    // (Not `clear`, which is Graphics' own.)
    clearRoster = () => {
        this.dying.forEach((card, entityId) => this.endDeath(entityId, card))
        this.slots.forEach((slot, entityId) => {
            this.disposeSlot(slot)
            this.speechLane.forget(entityId)
            this.damageFx.forget(entityId)
        })
        this.slots.clear()
        this.order = []
        this.selectMember(undefined)
        this.drawPanel(0)
    }

    // A card carries two different offers depending on who is on it. The LORD is the one member the
    // player never gives orders to, so his card opens the follow-cam PiP and lets you watch him.
    // Everyone else is under command, so their card opens THEIR command wheel above the strip --
    // which is the whole point of a roster you can reach an off-screen member through.
    private onSlotClick = (entityId: EntityId) => {
        const slot = this.slots.get(entityId)
        if (!slot) {
            return
        }
        // Whatever the last tap put up comes down first -- both live in the slot above the strip, and
        // only one card is ever the active one. It also makes tapping the ACTIVE card a toggle.
        const wasSelected = this.selectedEntityId === entityId
        this.guiManager.closeRosterSelection()
        if (wasSelected) {
            return
        }
        // Set AFTER the close above: closeRosterSelection clears the selection on its way out.
        this.selectMember(entityId)
        if (slot.role === "lord") {
            this.guiManager.openFollowCam(entityId, slot.name)
        } else {
            this.guiManager.openRosterCommandWheel(entityId, slot.role)
        }
    }

    // The one place the active card changes. Besides lighting the card itself, the selected member is
    // outlined OUT IN THE WORLD with the same glow a pointed-at entity gets, so it is obvious which
    // character on screen the card belongs to. Everything that selects or deselects goes through here
    // so the world glow can never outlive the card highlight.
    private selectMember = (entityId?: EntityId) => {
        if (this.selectedEntityId === entityId) {
            return
        }
        this.selectedEntityId = entityId
        this.applySelection()
        this.guiManager.highlightEmberMember(entityId)
    }

    // Called by GuiManager when whatever the card opened closes again (the follow-cam, or that
    // member's command wheel), so the highlighted card returns to normal.
    clearSelection = () => {
        this.selectMember(undefined)
    }

    // --- What the strip currently holds. Live cards plus the ones still breaking apart, because to
    // everything outside this widget (its width, the gap it is holding) those count as cards too.
    cardCount = (): number => this.slots.size + this.dying.size

    isDying = (entityId: EntityId): boolean => this.dying.has(entityId)

    // Which column a card occupies, living or dying; -1 if it isn't on the strip at all.
    columnOf = (entityId: EntityId): number =>
        this.slots.get(entityId)?.column ?? this.dying.get(entityId)?.column ?? -1

    // "greying" | "shattering" for a card on its way out, null for anything else.
    deathPhase = (entityId: EntityId): "greying" | "shattering" | null => {
        const card = this.dying.get(entityId)
        return card ? (card.shattered ? "shattering" : "greying") : null
    }

    // Rendered panel extents (in the parent's coordinate space, i.e. including WIDGET_SCALE), so
    // GuiManager can right-/bottom-align the strip as the card count changes. Measured from this
    // widget's origin (x/y) to the panel's right / bottom edge.
    rightExtent = (): number => {
        // Cards still breaking apart hold their column, so they are still part of the strip's width.
        const count = this.slots.size + this.dying.size
        return count < 1 ? 0 : (count * SLOT_W + PANEL_PAD) * WIDGET_SCALE
    }

    bottomExtent = (): number => (SLOT_H + PANEL_PAD) * WIDGET_SCALE

    // Distance from this widget's origin (y) up to the panel's top edge, INCLUDING the reserved
    // speech lane above it. Reserved whether or not a bubble is showing, so whatever sits above the
    // roster (the follow-cam PiP) doesn't jump every time someone speaks.
    topExtent = (): number => (PANEL_PAD + SPEECH_LANE_H) * WIDGET_SCALE

    // Top-centre of one member's card, in this widget's PARENT coordinate space (i.e. including this
    // widget's position and scale), plus the card's role colour. Null when that member has no card.
    // Used to draw a connector line from whatever the card opened back down to the card.
    //
    // `edge` picks which top the point sits on. The follow-cam's line is drawn OVER the strip and
    // stops at the card itself; a roster command wheel's line is drawn UNDER it (so a speech bubble
    // reads over the line) and has to stop at the panel's outer edge, or its end is buried.
    cardAnchor = (
        entityId: EntityId | undefined,
        edge: "card" | "panel" = "card",
    ): { x: number; y: number; color: number } | null => {
        if (!entityId) {
            return null
        }
        const slot = this.slots.get(entityId)
        if (!slot) {
            return null
        }
        // The card's RESTING centre: reading container.x would make the connector line wobble along
        // with a damage shake.
        const localCenterX = slot.baseX + SLOT_W / 2
        return {
            x: this.x + localCenterX * this.scale.x,
            y: this.y - (edge === "panel" ? PANEL_PAD * this.scale.y : 0),
            color: ROLE_COLORS[slot.role],
        }
    }

    // The selected card's anchor, for the follow-cam PiP's connector line.
    selectedCardAnchor = (): { x: number; y: number; color: number } | null =>
        this.cardAnchor(this.selectedEntityId)

    private applySelection = () => {
        this.slots.forEach((slot, entityId) => {
            const selected = this.selectedEntityId === entityId
            if (selected) {
                this.drawSelection(slot.selection, slot.role)
            }
            slot.selection.visible = selected
        })
    }
}
